import {
  ClassEnrollmentStatus,
  GetViewerLearningDetailsQuery,
  ProgramEnrollmentStatus,
  ProofOfCompletionType,
} from '@sandtech-hub-monorepo/ehub-client';
import { Button, Card, CardContent } from '@sandtech-hub-monorepo/ui-kit';
import {
  ExternalApplicationLinks,
  getIconFromAbbreviation,
  UniversityIcon,
} from '@sandtech-hub-monorepo/ui-kit-site';
import { ProgramEnrollmentStatusLabel } from './ProgramEnrollmentStatusLabel';
import { PlannedCoursesView } from './PlannedCoursesView';
import { PlannedCourseButton } from './PlannedCourseButton';
import { ProgramEnrollmentAttendance } from './ProgramEnrollmentAttendance';
import { ProofOfCompletionBanner } from '../../ProofOfCompletion/Banner';
import { AWSCloudPractitionerModal } from '../../ProofOfCompletion/AWSCloudPractitionerModal';
import { AWSCloudSolutionsArchitectModal } from '../../ProofOfCompletion/AWSCloudSolutionsArchitectModal';
import { getExplanationTextByProofOfCompletionType } from '../../ProofOfCompletion/util';
import { useMemo, useState } from 'react';
import { SalesforceAssociateModal } from '../../ProofOfCompletion/SalesforceAssociateModal';
import { SalesforceAdministratorModal } from '../../ProofOfCompletion/SalesforceAdministratorModal';
import { ExternalLinkIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { OpportunitiesModal } from './OpportunitiesModal';
import { Modal } from '@sandtech-hub-monorepo/ui-kit';
import { GraduatedCertificateCard } from './GraduatedCertificateCard';
import { ProgramProgressBar } from './ProgramProgressBar';
import { AppRoutesPaths } from '../../../../../../../apps/central-hub/src/routes/types';

type ProgramEnrollmentViewType = NonNullable<
  NonNullable<GetViewerLearningDetailsQuery['viewer']['programEnrollments']>
>[number];

type ProgramEnrollmentsViewProps = {
  enrollment: ProgramEnrollmentViewType;
  onlyOngoingCourses?: boolean;
  showOngoingCoursesToggle?: boolean;
  coursesHiddenByDefault?: boolean;
};

export const ProgramEnrollmentView = ({
  enrollment,
  onlyOngoingCourses = false,
  showOngoingCoursesToggle = false,
  coursesHiddenByDefault = false,
}: ProgramEnrollmentsViewProps) => {
  const [isOpportunitiesModalOpen, setIsOpportunitiesModalOpen] =
    useState(false);

  const canShowUniversityUpsellAfter = useMemo(() => {
    const classesWithUniversityUpsellAfter = enrollment.plannedCourses
      .filter(
        (c) => !!c.enrollment?.currentClass?.class.canShowUniversityUpsellAfter
      )
      .map((c) => c.enrollment?.currentClass);

    return (
      !!classesWithUniversityUpsellAfter.length &&
      classesWithUniversityUpsellAfter.every(
        (c) => c?.status === ClassEnrollmentStatus.Graduated
      ) &&
      Date.now() >=
        Math.max(
          ...classesWithUniversityUpsellAfter.map((c) =>
            new Date(c?.class.canShowUniversityUpsellAfter).valueOf()
          )
        )
    );
  }, [enrollment.plannedCourses]);

  const courseEnrollmentRequiringProofOfCompletion = useMemo(
    () =>
      enrollment.plannedCourses.find(
        (c) => c.enrollment?.currentClass?.class.requiresProofOfCompletionAfter
      )?.enrollment,
    [enrollment.plannedCourses]
  );

  const isProofOfCompletionInReview = useMemo(() => {
    return Boolean(
      courseEnrollmentRequiringProofOfCompletion?.currentClass
        ?.proofOfCompletion &&
        courseEnrollmentRequiringProofOfCompletion?.currentClass.status ===
          ClassEnrollmentStatus.Ongoing
    );
  }, [courseEnrollmentRequiringProofOfCompletion]);

  const shouldDisplayProofOfCompletionBanner = useMemo(() => {
    // If the learner's Proof of Completion was accepted or rejected, we don't show the banner anymore.
    if (
      courseEnrollmentRequiringProofOfCompletion?.currentClass?.status &&
      [ClassEnrollmentStatus.Failed, ClassEnrollmentStatus.Graduated].includes(
        courseEnrollmentRequiringProofOfCompletion?.currentClass?.status
      )
    ) {
      return false;
    }

    const startDateOfSubmittingProofOfCompletion =
      courseEnrollmentRequiringProofOfCompletion?.currentClass?.class
        .requiresProofOfCompletionAfter;

    // If the course does not require a proof of completion we don't show the banner. We double check for both completion type and startDate.
    if (
      courseEnrollmentRequiringProofOfCompletion?.currentClass?.class
        .proofOfCompletionType === ProofOfCompletionType.None ||
      !startDateOfSubmittingProofOfCompletion
    ) {
      return false;
    }

    // If we're past the time when the proof of completion flow should start for a course, we display the banner.
    const submissionTime = new Date(
      startDateOfSubmittingProofOfCompletion
    ).getTime();
    const currentTime = new Date().getTime();

    return submissionTime <= currentTime;
  }, [courseEnrollmentRequiringProofOfCompletion]);

  return (
    <Card className="rounded-sm border-border-secondary">
      <CardContent className="p-6 rounded-sm">
        <div className="flex flex-col items-start sm:flex-row gap-2 md:gap-6">
          <div className="flex gap-2 items-center">
            {getIconFromAbbreviation(enrollment.program.abbreviation)}
            <span className="block sm:hidden text-xl font-semibold text-heading">
              {enrollment.program.name}
              <ProgramEnrollmentStatusLabel status={enrollment.status} />
            </span>
          </div>
          <div className="flex flex-col w-full gap-2">
            <div className="flex gap-4 sm:flex-row flex-col justify-between">
              <div className="flex flex-col gap-4 sm:gap-1">
                <div className="hidden sm:flex gap-3 text-xl font-semibold text-heading">
                  {enrollment.program.name}
                  <ProgramEnrollmentStatusLabel status={enrollment.status} />
                </div>
                <p className="text-sm text-popover-foreground">
                  {enrollment.program.description}
                </p>

                {enrollment.program.abbreviation === 'ALX_PATHWAYS' && (
                  <ProgramEnrollmentAttendance />
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:items-start">
                {enrollment.status === ProgramEnrollmentStatus.Ongoing && (
                  <Link to={AppRoutesPaths.Community}>
                    <Button className="flex gap-2">Go to Community</Button>
                  </Link>
                )}
                {!enrollment.program.hasMultiCourseDisplay && (
                  <PlannedCourseButton
                    status={
                      enrollment?.plannedCourses?.[0]?.enrollment?.currentClass
                        ?.status
                    }
                    link={
                      enrollment?.plannedCourses?.[0]?.enrollment?.currentClass
                        ?.lmsClassLocation?.courseLink ?? '/'
                    }
                  />
                )}
              </div>
            </div>

            {enrollment.status === ProgramEnrollmentStatus.Graduated &&
              enrollment.program.issuedCertificate?.imageUrl && (
                <GraduatedCertificateCard
                  programName={enrollment.program.name}
                  issuedCertificate={enrollment.program.issuedCertificate}
                />
              )}

            {enrollment.status === ProgramEnrollmentStatus.Ongoing &&
              enrollment.program.hasMultiCourseDisplay && (
                <ProgramProgressBar
                  plannedCourses={enrollment.plannedCourses}
                />
              )}

            {shouldDisplayProofOfCompletionBanner && (
              <ProofOfCompletionBanner
                classEnrollmentId={
                  courseEnrollmentRequiringProofOfCompletion!.currentClass!.id
                }
                modal={(() => {
                  const proofType =
                    courseEnrollmentRequiringProofOfCompletion!.currentClass!
                      .class.proofOfCompletionType;
                  if (proofType === ProofOfCompletionType.AwsCloudComputing) {
                    return AWSCloudPractitionerModal;
                  } else if (
                    proofType === ProofOfCompletionType.AwsSolutionsArchitect
                  ) {
                    return AWSCloudSolutionsArchitectModal;
                  } else if (
                    proofType === ProofOfCompletionType.SalesforceAssociate
                  ) {
                    return SalesforceAssociateModal;
                  } else {
                    return SalesforceAdministratorModal;
                  }
                })()}
                // If the user has already submitted the proof of completion, but it's still in review, we change the banner text.
                text={
                  isProofOfCompletionInReview
                    ? 'Thanks for submitting your certificate! If you need to resubmit for any reason, please use the form provided.'
                    : getExplanationTextByProofOfCompletionType(
                        courseEnrollmentRequiringProofOfCompletion!
                          .currentClass!.class.proofOfCompletionType
                      )
                }
                ctaText={
                  isProofOfCompletionInReview
                    ? 'Re-submit now'
                    : 'Submit it now'
                }
              />
            )}

            {canShowUniversityUpsellAfter && (
              <div className="flex flex-wrap gap-6 p-6 mt-2 rounded-lg bg-background-tertiary border border-primary items-center">
                <div className="bg-background rounded-full p-2 border-2 border-primary text-primary">
                  <UniversityIcon />
                </div>
                <div className="flex-1 flex flex-col gap-2">
                  <h2 className="font-semibold">
                    Please select your program on the FIT website
                  </h2>
                  <p className="text-sm">
                    Ready to kickstart your university journey? Click{' '}
                    <span className="font-semibold">Go to FIT website</span> to
                    join the Frontier Institute of Technology.
                    <div className="text-muted-foreground">
                      Not sure yet? Explore more university options and
                      resources by clicking{' '}
                      <Button
                        className="px-0"
                        variant="link"
                        onClick={() => setIsOpportunitiesModalOpen(true)}
                      >
                        here
                      </Button>
                    </div>
                  </p>
                </div>
                <Link
                  to={ExternalApplicationLinks.set}
                  target="_blank"
                  className="ml-auto"
                >
                  <Button variant="outline" className="flex gap-2">
                    Go to FIT website <ExternalLinkIcon />
                  </Button>
                </Link>
              </div>
            )}

            {enrollment.program.hasMultiCourseDisplay && (
              <PlannedCoursesView
                plannedCourses={enrollment.plannedCourses}
                onlyOngoingCourses={onlyOngoingCourses}
                showOngoingCoursesToggle={showOngoingCoursesToggle}
                coursesHiddenByDefault={coursesHiddenByDefault}
                isUniversityProgram={enrollment.program.isUniversityProgram}
              />
            )}
          </div>
        </div>
      </CardContent>

      {/* Opportunities Modal */}
      <Modal
        open={isOpportunitiesModalOpen}
        onOpenChange={setIsOpportunitiesModalOpen}
      >
        <OpportunitiesModal close={() => setIsOpportunitiesModalOpen(false)} />
      </Modal>
    </Card>
  );
};
